import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #transitionGenerations = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    let details = this.#getDetailsElement(event);
    while (details) {
      const expanded = details.querySelector('summary')?.getAttribute('aria-expanded');
      if (expanded === 'true' || (expanded == null && details.open)) break;
      if (details === this.refs.details) return;
      details = details.parentElement?.closest('details');
    }
    if (!details) return;

    event.preventDefault();
    this.#close(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is logically open
   */
  get isOpen() {
    return this.refs.details.querySelector('summary')?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // The closing animation can retain details[open] after the menu is hidden.
    // Own the summary default action so a rapid reopen does not toggle it shut.
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    event?.preventDefault();
    const generation = (this.#transitionGenerations.get(details) ?? 0) + 1;
    this.#transitionGenerations.set(details, generation);
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      if (!this.isConnected || this.#transitionGenerations.get(details) !== generation) return;
      if (summary.getAttribute('aria-expanded') === 'true') details.classList.add('menu-open');
    });

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const restoreMainFocus = details === this.refs.details
      && details.contains(document.activeElement) && document.activeElement !== summary;
    const generation = (this.#transitionGenerations.get(details) ?? 0) + 1;
    this.#transitionGenerations.set(details, generation);
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      // Only this close may reset its drawer, even after another open/close.
      if (!this.isConnected || this.#transitionGenerations.get(details) !== generation) return;
      if (summary.getAttribute('aria-expanded') === 'true') return;

      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach((element) => {
          this.#transitionGenerations.set(element, (this.#transitionGenerations.get(element) ?? 0) + 1);
          reset(element);
        });
        const active = document.activeElement;
        if (restoreMainFocus && (!active || active === document.body
          || active === document.documentElement || details.contains(active))) {
          summary.focus();
        }
      } else if (this.isOpen) {
        trapFocus(this.refs.details);
      }
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
